import React, { ReactNode, useState } from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from '@cloudscape-design/components';

export type ClientSideTableProps = Omit<TableProps, 'items'> & {
  data: unknown[];
  title: string;
  error: Error | null;
  actions?: ReactNode;
  description?: ReactNode;
  sortColumn?: string;
  isDescending?: boolean;
  emptyText?: string;
  filteringDisabled?: boolean;
  preferencesDisabled?: boolean;
  paginationDisabled?: boolean;
  highlightedRow?: { columnId: string; value: string };
};

function EmptyState({
  title,
  action,
  error,
}: {
  title: string;
  action?: ReactNode;
  error: Error | null;
}) {
  return (
    <Box textAlign="center" color="inherit">
      <SpaceBetween size="m">
        <Box variant="strong" textAlign="center" color="inherit">
          {title}
        </Box>
        {error && (
          <Alert
            statusIconAriaLabel="Error"
            type="error"
            header="Error loading data"
          >
            {error.message}
          </Alert>
        )}
        <Box padding="m">{action}</Box>
      </SpaceBetween>
    </Box>
  );
}

export const ClientSideTable = (clientSideTableProps: ClientSideTableProps) => {
  const [preferences, setPreferences] =
    useState<CollectionPreferencesProps.Preferences>({
      pageSize: 10,
      contentDisplay: clientSideTableProps.columnDefinitions.map((col) => ({
        id: col.id || '',
        visible: true,
      })),
    });

  // Create modified column definitions that bold text for highlighted rows
  const modifiedColumnDefinitions = clientSideTableProps.highlightedRow
    ? clientSideTableProps.columnDefinitions.map((col) => ({
        ...col,
        cell: (item: any) => {
          const isHighlighted =
            item[clientSideTableProps.highlightedRow!.columnId] ===
            clientSideTableProps.highlightedRow!.value;

          const cellContent = col.cell(item);

          return isHighlighted ? <strong>{cellContent}</strong> : cellContent;
        },
      }))
    : clientSideTableProps.columnDefinitions;

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(clientSideTableProps.data, {
    filtering: {
      empty: (
        <EmptyState
          title={clientSideTableProps.emptyText || 'No instances'}
          error={clientSideTableProps?.error}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Clear filter
            </Button>
          }
          error={null}
        />
      ),
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        isDescending:
          clientSideTableProps.isDescending !== undefined
            ? clientSideTableProps.isDescending
            : true,
        sortingColumn: {
          sortingField: clientSideTableProps.sortColumn || 'createdDate',
        },
      },
    },
    selection: {},
  });

  const { selectedItems } = collectionProps;
  return (
    <Table
      {...collectionProps}
      {...clientSideTableProps}
      columnDefinitions={modifiedColumnDefinitions}
      loading={clientSideTableProps.loading}
      wrapLines
      header={
        <Header
          counter={
            selectedItems?.length
              ? `(${selectedItems?.length}/${clientSideTableProps.data.length})`
              : `(${clientSideTableProps.data.length})`
          }
          actions={clientSideTableProps.actions}
          description={clientSideTableProps.description}
        >
          {clientSideTableProps.title}
        </Header>
      }
      columnDisplay={preferences.contentDisplay}
      items={items}
      pagination={
        !clientSideTableProps.paginationDisabled ? (
          <Pagination
            {...paginationProps}
            ariaLabels={{
              nextPageLabel: 'Next page',
              paginationLabel: 'Table pagination',
              previousPageLabel: 'Previous page',
              pageLabel: (pageNumber) => `Page ${pageNumber}`,
            }}
          />
        ) : undefined
      }
      filter={
        !clientSideTableProps.filteringDisabled ? (
          <TextFilter
            {...filterProps}
            countText={`${filteredItemsCount} matches`}
            filteringAriaLabel="Filter items"
            filteringText={filterProps.filteringText}
            filteringPlaceholder={'Find items'}
          />
        ) : undefined
      }
      preferences={
        !clientSideTableProps.preferencesDisabled ? (
          <CollectionPreferences
            title="Preferences"
            cancelLabel="Cancel"
            confirmLabel="Confirm"
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 10, label: '10 items' },
                { value: 20, label: '20 items' },
              ],
            }}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
          />
        ) : undefined
      }
    />
  );
};
